import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import {
  advanceBookingDays,
  bookingWindowPhrase,
  calendarDayNames,
  describeSlot,
  hasBookableSlot,
  isSlotBookable,
  isWithinBookingWindow,
  monthGrid,
  monthNames,
  noticePhrase,
  slotStartIso,
  slotsFor,
} from '../../data/booking';
import { humanMinutes } from '../../data/progress';
import useAvailability from '../../hooks/useAvailability';
import {
  dateKey,
  prefetchAvailability,
  type AvailabilityItem,
  type BlockedSlot,
} from '../../services/availability';
import { getAccessToken } from '../../services/client';
import { appConfig } from '../../services/config';
import { colors, hairline, radii, screenPadding, spacing } from '../../theme';
import BottomSheet from '../ui/BottomSheet';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Skeleton, { useShimmer } from '../ui/Skeleton';
import Text from '../ui/Text';

/**
 * Most days the quick strip shows: two weeks. The strip covers the whole
 * booking window when it fits - today plus three days by default, and the 5 or
 * 8 ops may set from the admin console - and the calendar is drawn only for a
 * window longer than this.
 */
const MAX_STRIP_DAYS = 15;

/** Time buttons per row. */
const TIME_COLUMNS = 3;

export type Slot = {
  date: Date;
  time: string;
};

type Props = {
  visible: boolean;
  /**
   * Bumped by the cart's pull-to-refresh, to re-read a day this sheet has
   * already asked about - see `useAvailability`. Opening hours belong to the
   * city and ops edits them while people are picking a time.
   */
  reloadKey?: number;
  /** What is already chosen, so reopening does not start from scratch. */
  slot: Slot | null;
  onClose: () => void;
  onConfirm: (slot: Slot) => void;
  /**
   * Taken when somebody taps a slot their basket is too long for.
   *
   * The sheet can offer the other way out itself - tomorrow is a date, and
   * dates are what it draws - but it cannot change what is in the basket, so
   * the screen that owns the basket passes this. Omitted by the reschedule,
   * where the service is fixed and the button would be a lie.
   */
  onChooseShorter?: () => void;

  /**
   * A floor stricter than the booking notice, when the caller has one.
   *
   * A reschedule does. The server refuses a move onto a slot inside its own
   * cutoff - six hours by default, against the two this sheet normally
   * enforces - and answers `earliestNewSlotAt` so the picker can start where
   * it will be accepted rather than offering times the next call refuses.
   *
   * Undefined for the cart, which is the ordinary case and unchanged.
   */
  notBefore?: Date;
  /**
   * A line above the grid, written by whoever knows why the sheet is open.
   *
   * The reschedule allowance arrives as prose from the server - "Free to
   * move. 2 changes left on this booking." - deliberately, so the app does
   * not assemble that sentence out of a count and get the plural wrong.
   */
  note?: string;
  /**
   * Holds Confirm while the caller is still finding out what is allowed.
   *
   * Without it the sheet opens usable, and somebody quick could pick a slot
   * before the allowance lands - which is the failure this whole change
   * exists to remove, just moved a second later.
   */
  busy?: boolean;
  /**
   * The saved address the job would go to, as the SERVER's row id.
   *
   * What capacity is asked about - see `services/availability`, which explains
   * why it is an address id rather than a pin. Undefined disables the lookup
   * entirely and leaves every slot selectable, which is the behaviour that
   * shipped before capacity existed: an address not yet on the server (or a
   * signed-out customer) has nothing to ask about.
   *
   * The fetch lives in this component rather than in the cart because the DAY
   * being asked about is this sheet's own draft state. Lifting the date out
   * just to pass availability back down would make the caller re-render on
   * every tap of the date strip.
   */
  addressId?: string;
  /** Narrows capacity to one trade. Omitted for a basket spanning several. */
  serviceId?: string;
  /**
   * The basket, one entry per booking it becomes. Two services at 11:00 need
   * two professionals free at 11:00, so the cart sends what it holds rather
   * than asking about one trade.
   */
  items?: readonly AvailabilityItem[];
  /** The booking being moved, which must not count against its own slot. */
  bookingId?: string;
  /**
   * The caller books a NEW job, so it cannot offer times without an address.
   *
   * With no `addressId` the sheet used to draw the compiled 7:00 AM–8:00 PM
   * grid with every tile open: none of the city's hours, closed days or full
   * times applied, so a customer could pick 7:30 PM for a city that closes at
   * 5:00 and only hear "no" at checkout. With this set the sheet says to
   * choose an address instead of drawing a grid it cannot vouch for. A
   * reschedule always has its booking's address and leaves this off.
   */
  needsAddress?: boolean;
  /** Where "Choose address" goes, when `needsAddress` holds the grid back. */
  onChooseAddress?: () => void;
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Picking a date and a time, as a sheet over the cart.
 *
 * Both halves are on one surface rather than two steps. Choosing a day and
 * choosing an hour are one decision - people move between them, settling on a
 * Tuesday only once they see nothing good is free on Monday - and a wizard
 * makes that backtracking cost a screen each way.
 *
 * The strip and the calendar are the same control at two speeds: the strip
 * covers the next few days, which is when most bookings land, and the calendar
 * is there for the rest without making the common case scroll to it.
 *
 * Nothing is confirmed until Confirm. The sheet holds its own draft so backing
 * out leaves the cart's existing slot alone.
 */
function SlotSheet({
  visible,
  slot,
  onClose,
  onConfirm,
  notBefore,
  note,
  busy,
  addressId,
  serviceId,
  items,
  bookingId,
  reloadKey,
  needsAddress,
  onChooseAddress,
  onChooseShorter,
}: Props) {
  /*
   * Captured once per mount rather than read on each render. `new Date()` in
   * the render path would make "today" drift mid-session and re-run the
   * memos below on every frame.
   */
  const today = useMemo(() => startOfDay(new Date()), []);

  // The cart keeps this sheet mounted between opens; its cutoff must not freeze.
  const [openedAt, setOpenedAt] = useState(() => new Date());
  useEffect(() => {
    if (!visible) return;
    const refreshClock = () => setOpenedAt(new Date());
    refreshClock();
    const timer = setInterval(refreshClock, 30_000);
    return () => clearInterval(timer);
  }, [visible]);

  /* Days after today that may be booked - `booking.advanceBookingDays`. */
  const windowDays = advanceBookingDays();

  /* A day can be picked when it is inside the booking window and still has a
     slot left on it. */
  const selectable = (day: Date) =>
    isWithinBookingWindow(day, openedAt) &&
    hasBookableSlot(day, openedAt, notBefore);

  /*
   * Opens on the chosen slot's day while that day is still in the window,
   * otherwise on the next available day - late in the evening that is
   * tomorrow rather than a "Today" with nothing left on it. A chosen slot now
   * outside the window loses its time too, so Confirm cannot quietly move it
   * to another day.
   */
  const slotInWindow = slot
    ? isWithinBookingWindow(slot.date, openedAt)
    : false;
  const [date, setDate] = useState<Date>(() => {
    if (slot && slotInWindow) {
      return startOfDay(slot.date);
    }
    for (let i = 0; i <= windowDays; i++) {
      const day = new Date(today);
      day.setDate(day.getDate() + i);
      if (selectable(day)) {
        return day;
      }
    }
    return today;
  });
  const [time, setTime] = useState<string>(
    slotInWindow ? slot?.time ?? '' : '',
  );
  /*
   * The slot somebody tapped that their basket is too long for, while the
   * explanation is up. Null the rest of the time.
   *
   * Held here rather than raised through `services/dialog`, and drawn inside
   * this sheet rather than as a `ConfirmSheet` over it, because every sheet
   * in this app is a native Modal and iOS will not present one on top of
   * another - see the note on `BottomSheet.onClosed`. The last time that was
   * tried the second sheet never appeared and its window sat over the app
   * swallowing touches. A panel inside the Modal already on screen looks the
   * same and cannot do that.
   */
  const [explain, setExplain] = useState<BlockedSlot | null>(null);

  /*
   * Which times on the shown day are already taken.
   *
   * Asked only while the sheet is open - a closed sheet has no day on screen
   * to ask about, and fetching for one would spend a request on nothing every
   * time the cart re-rendered.
   */
  const {
    window: bookingWindow,
    fullTimes,
    blockedTimes,
    closed: dayClosed,
    loading: availabilityLoading,
    checking: availabilityChecking,
    failed: availabilityFailed,
    retry: retryAvailability,
  } = useAvailability(
    visible ? date : null,
    addressId,
    { serviceId, items, bookingId },
    reloadKey,
  );

  /*
   * Ask about the days on offer before they are tapped.
   *
   * Runs as soon as the sheet is mounted - in the cart that is while the
   * customer is still reading their basket - one day after another, starting
   * with the day it opens on. Each answer is kept for a minute (see
   * `services/availability`), so opening the sheet and moving along the strip
   * draw from answers already in hand instead of waiting on each tap.
   */
  const prefetchKey = JSON.stringify([
    addressId ?? null,
    serviceId ?? null,
    items ?? null,
    bookingId ?? null,
    windowDays,
  ]);
  useEffect(() => {
    if (!addressId || !getAccessToken()) {
      return;
    }
    const days: Date[] = [];
    for (let i = 0; i <= Math.min(windowDays, MAX_STRIP_DAYS - 1); i++) {
      const day = new Date(today);
      day.setDate(day.getDate() + i);
      if (selectable(day)) {
        days.push(day);
      }
    }
    const first = days.findIndex(day => day.getTime() === date.getTime());
    const ordered =
      first > 0 ? [...days.slice(first), ...days.slice(0, first)] : days;
    prefetchAvailability(
      ordered.map(day => ({
        date: dateKey(day),
        addressId,
        serviceId,
        items,
        bookingId,
      })),
    );
    /* Keyed on `prefetchKey`: the same basket and address is asked about once
       per mount, not once per render. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetchKey]);

  /*
   * The hours this city actually opens, or the compiled grid while the server
   * has no opinion - `slotsFor` decides which and defaults to the compiled
   * one. This sheet rendered `timeSlots` directly, which is how a city opening
   * at 09:00 kept offering 07:00 with nobody on duty to take it.
   */
  const allSlots = useMemo(() => slotsFor(bookingWindow), [bookingWindow]);
  /*
   * The grid as it is actually drawn.
   *
   * With `showBlockedSlots` off - the default - a start the basket cannot
   * finish before closing is left out rather than drawn dead. Ops can turn
   * them back on from the console; nothing about them is deleted, and the
   * explanation below still exists for the day they are.
   *
   * Only the RULE-closed ones go. A fully-booked tile stays, struck through,
   * because somebody cancelling frees it and a day with none left is not the
   * same as a day that ends early.
   */
  const showBlocked = appConfig().showBlockedSlots;
  /*
   * Times that have already passed today ARE drawn - greyed and not
   * tappable, like any other time that cannot be booked - so the customer
   * sees the whole day rather than a grid that starts at an odd hour. The
   * client asked for this: a slot they remember seeing earlier should still
   * be there, visibly gone, rather than vanish.
   */
  const slots = useMemo(
    () => allSlots.filter(option => showBlocked || !blockedTimes.has(option)),
    [allSlots, showBlocked, blockedTimes],
  );
  /* The ones still ahead - what decides whether anything is left today. */
  const upcomingSlots = useMemo(
    () =>
      slots.filter(
        option =>
          new Date(slotStartIso(date, option)).getTime() > openedAt.getTime(),
      ),
    [slots, date, openedAt],
  );

  /*
   * How many dead tiles are actually ON SCREEN.
   *
   * Not `fullTimes.size`, which is what this used to ask. The server puts
   * every rule-blocked start into `fullSlots` as well, and with
   * `showBlockedSlots` off those tiles are not drawn - so the legend
   * announced crossed-out times on a grid that had none, which reads as the
   * app hiding something.
   *
   * Counted over the VISIBLE grid, so it stays right in both states: with
   * the setting on, a blocked tile is drawn and struck, and it counts.
   */
  const struckCount = useMemo(
    () => slots.filter(option => fullTimes.has(option)).length,
    [slots, fullTimes],
  );

  /*
   * Why the day ran out, taken from the server's own answer rather than
   * assembled here - the same numbers the tile popup quotes. Any blocked
   * entry will do: they all carry the basket's length, and the reason is the
   * same for every start the closing time took.
   */
  const dayGoneReason = useMemo(() => {
    const entry = [...blockedTimes.values()].find(
      item => item.reason === 'past_closing',
    );
    return entry
      ? `This booking needs ${humanMinutes(
          entry.visitMinutes,
        )} and our professionals finish for the day before that.`
      : 'Every time left today is too soon for us to find you a professional.';
  }, [blockedTimes]);
  /** Which month the calendar is showing - not necessarily the chosen one. */
  const [month, setMonth] = useState<Date>(
    new Date(date.getFullYear(), date.getMonth(), 1),
  );

  const strip = useMemo(
    () =>
      Array.from(
        { length: Math.min(windowDays + 1, MAX_STRIP_DAYS) },
        (_, i) => {
          const day = new Date(today);
          day.setDate(day.getDate() + i);
          return day;
        },
      ),
    [today, windowDays],
  );

  /* The calendar only earns its place when the window runs past the strip. */
  const showCalendar = windowDays + 1 > strip.length;

  const cells = useMemo(
    () => monthGrid(month.getFullYear(), month.getMonth()),
    [month],
  );

  const stepMonth = (delta: number) =>
    setMonth(
      current => new Date(current.getFullYear(), current.getMonth() + delta, 1),
    );

  /*
   * The panel is about ONE start on ONE day. Changing either leaves it
   * describing a tile that is no longer on screen, so it goes.
   */
  useEffect(() => setExplain(null), [date]);

  const pick = (day: number) => {
    const next = new Date(month.getFullYear(), month.getMonth(), day);
    if (next < today || !isWithinBookingWindow(next, openedAt)) {
      return;
    }
    setDate(next);
  };

  /*
   * Confirm needs a time that is still bookable, not merely a time.
   *
   * The grid disables what cannot be picked, but a selection made BEFORE the
   * date changed can survive into a day where it no longer qualifies: pick
   * 8:00 AM for tomorrow, switch the date to today, and the button would
   * otherwise still send a slot that has gone. Re-asking here means the rule
   * is enforced where it matters - at the point of sending - rather than only
   * where it is drawn.
   */
  const inWindow = isWithinBookingWindow(date, openedAt);
  /*
   * And one that is known to be free. Until the day's answer arrives there is
   * nothing to confirm against, and a time picked before it arrived can turn
   * out to be full - confirming either sent the customer on to a checkout
   * that then refused the slot.
   */
  const timeFull = Boolean(time) && (dayClosed || fullTimes.has(time));
  const waitingForAddress = Boolean(needsAddress) && !addressId;
  const ready =
    !waitingForAddress &&
    Boolean(time) &&
    inWindow &&
    !availabilityLoading &&
    !availabilityChecking &&
    !availabilityFailed &&
    !timeFull &&
    isSlotBookable(date, time, openedAt, notBefore);

  return (
    <BottomSheet visible={visible} onClose={onClose} heightRatio={0.86}>
      <View style={styles.header}>
        <Text variant="titleLg" style={styles.headerTitle}>
          Select Date & Time
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={onClose}
          hitSlop={10}
        >
          <Icon
            name="plus"
            size={18}
            color={colors.inkMuted}
            strokeWidth={2.2}
            style={styles.closeGlyph}
          />
        </Pressable>
      </View>

      <ScrollView
        contentContainerStyle={styles.body}
        showsVerticalScrollIndicator={false}
      >
        {/*
          What the caller knows and this sheet does not.

          Server prose, shown verbatim - see `note`. Above the grid rather
          than beside Confirm, because it frames the choice being made rather
          than footnoting it.
        */}
        {note ? (
          <Text variant="caption" color={colors.inkMuted} style={styles.note}>
            {note}
          </Text>
        ) : null}

        {/* --------------------------------------------------- the day strip */}
        <Text variant="labelSm" style={styles.label}>
          Select Date
        </Text>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.strip}
        >
          {strip.map((day, i) => {
            const active = sameDay(day, date);
            const caption =
              i === 0
                ? 'Today'
                : i === 1
                ? 'Tomorrow'
                : calendarDayNames[(day.getDay() + 6) % 7];
            /*
              A day with nothing left on it cannot be chosen. Late in the
              evening that is "Today" - every slot is inside the notice
              window - and letting it be tapped lands the customer on a grid
              where all thirty times are greyed out and nothing says why.
            */
            const full = !selectable(day);

            return (
              <Pressable
                key={day.toISOString()}
                accessibilityRole="button"
                accessibilityState={{ selected: active, disabled: full }}
                accessibilityLabel={`${caption} ${day.getDate()} ${
                  monthNames[day.getMonth()]
                }`}
                disabled={full}
                onPress={() => {
                  setDate(day);
                  setMonth(new Date(day.getFullYear(), day.getMonth(), 1));
                }}
                style={[
                  styles.stripDay,
                  active ? styles.stripDayOn : null,
                  full ? styles.stripDayOff : null,
                ]}
              >
                {/*
                  Three inks, one rule - and `full` is tested BEFORE `active`.

                  The two can be true at once, and today is exactly when: late
                  in the evening every slot is inside the notice window, so
                  the day the sheet opens on is both the selected day and a
                  day with nothing left. `stripDayOff` is last in the style
                  array above, so the pale disabled fill beats `stripDayOn`'s
                  forest green - and asking `active` first painted white ink
                  for a green tile that is not there. White on near-white:
                  the "Today" tile read as blank.

                  Whichever fill wins has to decide the ink, so the order
                  here matches the order up there.
                */}
                <Text
                  variant="caption"
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  minimumFontScale={0.8}
                  color={
                    full
                      ? colors.inkDisabled
                      : active
                      ? colors.onPrimary
                      : colors.inkMuted
                  }
                >
                  {caption}
                </Text>
                <Text
                  variant="titleLg"
                  color={
                    full
                      ? colors.inkDisabled
                      : active
                      ? colors.onPrimary
                      : colors.ink
                  }
                >
                  {day.getDate()}
                </Text>
                <Text
                  variant="caption"
                  color={
                    full
                      ? colors.inkDisabled
                      : active
                      ? colors.onPrimary
                      : colors.inkMuted
                  }
                >
                  {monthNames[day.getMonth()].slice(0, 3)}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        <Text variant="caption" color={colors.inkMuted} style={styles.notice}>
          {`Bookings are open for ${bookingWindowPhrase()}.`}
        </Text>

        {/* ---------------------------------------------------- the calendar */}
        {showCalendar ? (
          <>
            <View style={styles.monthRow}>
              <Text variant="subtitle">
                {monthNames[month.getMonth()]} {month.getFullYear()}
              </Text>
              <View style={styles.monthNav}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Previous month"
                  onPress={() => stepMonth(-1)}
                  hitSlop={10}
                  style={styles.monthStep}
                >
                  <Icon name="chevronLeft" size={18} color={colors.ink} />
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Next month"
                  onPress={() => stepMonth(1)}
                  hitSlop={10}
                  style={styles.monthStep}
                >
                  <Icon name="chevronRight" size={18} color={colors.ink} />
                </Pressable>
              </View>
            </View>

            <View style={styles.grid}>
              {calendarDayNames.map(name => (
                <View key={name} style={styles.cell}>
                  <Text
                    variant="caption"
                    align="center"
                    color={colors.inkMuted}
                  >
                    {name}
                  </Text>
                </View>
              ))}

              {cells.map((day, i) => {
                if (day === null) {
                  return <View key={`pad-${i}`} style={styles.cell} />;
                }

                const cellDate = new Date(
                  month.getFullYear(),
                  month.getMonth(),
                  day,
                );
                /* Gone, past the booking window, or has no slot left on it -
               today after the last cutoff is the last without being the
               first. */
                const past = cellDate < today || !selectable(cellDate);
                const active = sameDay(cellDate, date);

                return (
                  <Pressable
                    key={`day-${day}`}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active, disabled: past }}
                    accessibilityLabel={`${day} ${
                      monthNames[month.getMonth()]
                    }`}
                    disabled={past}
                    onPress={() => pick(day)}
                    style={styles.cell}
                  >
                    <View
                      style={[styles.dayDot, active ? styles.dayDotOn : null]}
                    >
                      <Text
                        variant="bodySm"
                        align="center"
                        /* `colors.border` here was green100 on a near-white
                       calendar - 1.1:1, which is not dim, it is blank. */
                        color={
                          active
                            ? colors.onPrimary
                            : past
                            ? colors.inkDisabled
                            : colors.ink
                        }
                      >
                        {day}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          </>
        ) : null}

        {/* -------------------------------------------------------- the time */}
        <Text variant="labelSm" style={styles.label}>
          Select Time
        </Text>

        {/*
          The loader, in the shape of the grid it stands in for.

          Drawn only while there is no answer at all for this day: a kept
          answer is shown at once and re-checked underneath. The tiles are the
          real tiles' size, so nothing jumps when the times land, and none of
          them can be tapped - a time picked here would be a guess.
        */}
        {waitingForAddress ? (
          <View style={styles.addressGate}>
            <Text variant="bodySm" color={colors.inkBody}>
              Choose your address to see the times we can come. Opening hours
              and free slots depend on where the visit is.
            </Text>
            {onChooseAddress ? (
              <Button
                label="Choose address"
                variant="secondary"
                onPress={onChooseAddress}
              />
            ) : null}
          </View>
        ) : availabilityLoading ? (
          <SlotGridLoader count={allSlots.length} />
        ) : availabilityFailed ? (
          <View style={styles.dayGone}>
            <Text variant="title">Could not check available times</Text>
            <Text variant="bodySm" color={colors.inkMuted}>
              Please try again before choosing a time.
            </Text>
            <Button
              label="Retry"
              variant="secondary"
              leadingIcon="refresh"
              onPress={retryAvailability}
            />
          </View>
        ) : upcomingSlots.length === 0 && allSlots.length > 0 ? (
          /*
           * Every start on the day went, and an empty box reads as a broken
           * screen. This is the tile popup's own sentence, said once for the
           * whole day - which is the case it was really written for: a
           * two-hour basket opened in the evening has nothing good left.
           */
          <View style={styles.dayGone}>
            <Text variant="title">No time left today for this booking</Text>
            <Text variant="bodySm" color={colors.inkMuted}>
              {dayGoneReason}
            </Text>
            <Button
              label="See tomorrow"
              onPress={() => {
                const next = new Date(date);
                next.setDate(next.getDate() + 1);
                setTime('');
                setDate(next);
              }}
            />
            {onChooseShorter ? (
              <Button
                label="Choose a shorter service"
                variant="secondary"
                onPress={onChooseShorter}
              />
            ) : null}
          </View>
        ) : (
          <View style={styles.times}>
            {slots.map(option => {
              const active = option === time;
              /*
              Two different reasons a tile can be dead, kept apart.

              `tooSoon` is the clock - the booking notice, or a reschedule's
              stricter cutoff. It resolves by looking further ahead.

              `full` is capacity: everybody who could do this job at this hour
              is already doing one. Looking further ahead is the ONLY remedy,
              and no amount of waiting reopens it.

              They were one flag when the clock was the only rule. Merging
              them again would tell somebody staring at a booked-out morning
              to "book at least 2 hours ahead", which is advice that cannot
              work and reads as the app being broken.
            */
              const tooSoon = !isSlotBookable(
                date,
                option,
                openedAt,
                notBefore,
              );
              const full = !tooSoon && Boolean(fullTimes?.has(option));
              /*
                A third reason, and the only one with something to say.

                The server marks a start the BASKET cannot fit - the city
                shuts before the work would end - and sends the arithmetic
                with it. Those starts arrive inside `fullSlots` too, so a
                build that ignores this draws them dead and silent, which is
                where this came from.

                The tile stays tappable, unlike a full one. There is nothing
                to tell somebody about an hour everybody is busy in; there is
                something to tell them here, and a disabled Pressable cannot
                be tapped to hear it.
              */
              const blockedBy = blockedTimes.get(option);
              const unavailable = tooSoon || full;
              return (
                <Pressable
                  key={option}
                  accessibilityRole="button"
                  accessibilityState={{
                    selected: active,
                    disabled: unavailable && !blockedBy,
                  }}
                  accessibilityLabel={
                    blockedBy
                      ? `${option}, too late for this booking - tap to see why`
                      : full
                      ? `${option}, fully booked`
                      : option
                  }
                  disabled={unavailable && !blockedBy}
                  onPress={() =>
                    blockedBy ? setExplain(blockedBy) : setTime(option)
                  }
                  style={[
                    styles.time,
                    active ? styles.timeOn : null,
                    unavailable ? styles.timeOff : null,
                  ]}
                >
                  {/* `unavailable` first, for the same reason as the day strip
                    above: `timeOff` is last in the style array so it beats
                    `timeOn`'s fill, and a time can be both. Keeping a picked
                    8:00 AM and then switching to a day where 8:00 AM has gone
                    leaves this tile selected AND unavailable - asking
                    `active` first painted white ink on the pale fill. */}
                  {/* Struck through only when FULL. Greying alone cannot tell
                    the two reasons apart, and the strike is the conventional
                    mark for "this one is gone" rather than "not yet". */}
                  <Text
                    variant="bodySm"
                    align="center"
                    style={full ? styles.timeFullText : undefined}
                    color={
                      unavailable
                        ? colors.inkDisabled
                        : active
                        ? colors.onPrimary
                        : colors.ink
                    }
                  >
                    {option}
                  </Text>
                  {/* How much of the day is left at this start, so the
                    pattern is readable down the column without tapping
                    anything - "1 hr", "30 min", then nothing at all. */}
                  {blockedBy && blockedBy.minutesLeft !== null ? (
                    <Text
                      variant="caption"
                      align="center"
                      color={colors.inkDisabled}
                    >
                      {blockedBy.minutesLeft > 0
                        ? `${humanMinutes(blockedBy.minutesLeft)} left`
                        : 'closed'}
                    </Text>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        )}

        {/*
          The rule, stated once, rather than a tooltip on thirty dead buttons.

          Only drawn where something is actually greyed out - on a day far
          enough ahead that every slot is open, a notice about notice is
          noise.
        */}
        {!availabilityFailed &&
        slots.some(
          option => !isSlotBookable(date, option, openedAt, notBefore),
        ) ? (
          <Text variant="caption" color={colors.inkMuted} style={styles.notice}>
            {/*
              Which rule is doing the greying out, named correctly.

              Citing the booking notice while a reschedule's six-hour cutoff
              is what actually closed those slots would tell somebody to wait
              two hours for a time that will still be refused.

              The notice itself comes from `noticePhrase()`, not from the
              compiled constant. Ops can change it, and a line that kept
              saying "2 hours" while the grid opened at one would be this
              screen contradicting itself.
            */}
            {notBefore
              ? 'Earlier times are inside the window for changing this booking.'
              : noticePhrase()
              ? `Times need at least ${noticePhrase()} so we can assign a professional.`
              : 'Times that have already passed cannot be booked.'}
          </Text>
        ) : null}

        {/*
          The capacity line, separate from the notice line above.

          Both can be true at once - an early morning inside the notice AND a
          booked-out afternoon - and they are two different facts, so they get
          a line each rather than one sentence trying to cover both.
        */}
        {/* A day ops closed says so, rather than looking booked out. */}
        {!availabilityFailed && dayClosed ? (
          <Text variant="caption" color={colors.inkMuted} style={styles.notice}>
            We are not taking bookings on this day. Please pick another date.
          </Text>
        ) : !availabilityFailed && struckCount > 0 ? (
          <Text variant="caption" color={colors.inkMuted} style={styles.notice}>
            Crossed-out times are fully booked. Try another time or day.
          </Text>
        ) : null}
      </ScrollView>

      <View style={styles.footer}>
        <Button
          label="Confirm"
          disabled={!ready || busy}
          onPress={() => {
            const now = new Date();
            if (ready && !busy && isSlotBookable(date, time, now, notBefore)) {
              onConfirm({ date, time });
            } else {
              setOpenedAt(now);
            }
          }}
        />
        <Text variant="caption" align="center" color={colors.inkMuted}>
          {ready
            ? describeSlot(date, time, today)
            : waitingForAddress
            ? 'Choose your address first'
            : availabilityLoading || availabilityChecking
            ? 'Checking which times are free…'
            : availabilityFailed
            ? 'Retry to check available times'
            : dayClosed
            ? 'We are not taking bookings on this day - pick another date'
            : timeFull
            ? 'That time is fully booked - pick another'
            : !inWindow
            ? `Pick a day within ${bookingWindowPhrase()}`
            : time
            ? 'That time has passed - pick another'
            : 'Pick a time to continue'}
        </Text>
      </View>

      {/*
        Why that tile is dead, and the two ways out of it.

        Both are things the customer can actually do. "Pick another slot" is
        not one of them when every later slot on the day is blocked for the
        same reason, which is the whole case this exists for: a two-hour
        basket opened at eight in the evening has no good tile left today.
      */}
      {explain ? (
        <View style={styles.explainScrim}>
          <View style={styles.explainCard}>
            <Text variant="title">
              {explain.reason === 'too_soon'
                ? 'That time is too soon'
                : explain.minutesLeft && explain.minutesLeft > 0
                ? `Only ${humanMinutes(explain.minutesLeft)} left then`
                : 'We are closed by then'}
            </Text>
            <Text variant="bodySm" color={colors.inkMuted}>
              {explain.reason === 'too_soon'
                ? 'We need a little notice to find you a professional. Please pick a later time.'
                : `This booking needs ${humanMinutes(
                    explain.visitMinutes,
                  )} and our professionals finish for the day before that. Pick an earlier time, tomorrow, or a shorter service.`}
            </Text>
            <View style={styles.explainActions}>
              <Button
                label="See tomorrow"
                onPress={() => {
                  const next = new Date(date);
                  next.setDate(next.getDate() + 1);
                  setExplain(null);
                  setTime('');
                  setDate(next);
                }}
              />
              {/* Only where the caller can actually change the basket. The
                reschedule cannot, and a button that does nothing is worse
                than one that is not there. */}
              {onChooseShorter ? (
                <Button
                  label="Choose a shorter service"
                  variant="secondary"
                  onPress={() => {
                    setExplain(null);
                    onChooseShorter();
                  }}
                />
              ) : null}
              <Button
                label="Close"
                variant="ghost"
                onPress={() => setExplain(null)}
              />
            </View>
          </View>
        </View>
      ) : null}
    </BottomSheet>
  );
}

/**
 * Placeholder tiles for the time grid while a day's answer is on its way,
 * with one line saying what is happening.
 */
function SlotGridLoader({ count }: { count: number }) {
  const pulse = useShimmer();
  return (
    <View
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel="Checking which times are free"
    >
      <View style={styles.times}>
        {Array.from({ length: Math.max(count, TIME_COLUMNS * 3) }, (_, i) => (
          <View key={i} style={[styles.time, styles.timeLoading]}>
            <Skeleton height={14} width="60%" radius={radii.sm} pulse={pulse} />
          </View>
        ))}
      </View>
      <Text variant="caption" color={colors.inkMuted} style={styles.notice}>
        Checking which times are free…
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
  },
  headerTitle: {
    flex: 1,
  },
  // A rotated plus - the icon set has no cross glyph.
  closeGlyph: {
    transform: [{ rotate: '45deg' }],
  },

  body: {
    paddingBottom: spacing.lg,
  },
  label: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
  },

  strip: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
  },
  /*
   * Wide enough for the longest label, "Tomorrow", on one line. At 64 its
   * last letter wrapped onto a second line, so that one tile stood taller and
   * read differently from every other day. One width for every tile keeps the
   * strip even; the labels are also held to a single line below.
   */
  stripDay: {
    width: 76,
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
    ...hairline,
  },
  stripDayOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  /* Nothing bookable left on this day - dimmed, not hidden, so the strip
     keeps its shape and the day is visibly there rather than missing.
     Ink and fill rather than `opacity`, for the reason on `timeOff` below. */
  /* Wins over `stripDayOn` - it is last in the style array - so it has to
     undo the selected border too, not just the fill. Leaving `stripDayOn`'s
     forest outline behind a pale fill drew a tile that looked selected and
     unavailable at the same time. */
  stripDayOff: {
    backgroundColor: colors.background,
    borderColor: colors.border,
  },

  monthRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
  },
  monthNav: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  monthStep: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* Seven equal columns. A percentage width rather than flex, because the
     cells wrap - flex would stretch a short final row. */
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: screenPadding,
  },
  cell: {
    width: `${100 / 7}%`,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xs,
  },
  dayDot: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayDotOn: {
    backgroundColor: colors.primary,
  },

  times: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
  },
  /*
   * Past, or inside the notice window. Same treatment for both: to a
   * customer they are equally unavailable.
   *
   * The dimming is in the INK (`inkDisabled`), not in an `opacity: 0.4` on
   * the whole tile as it used to be. Opacity multiplied against an already
   * muted green took the label to roughly 2:1 and faded the tile's own fill
   * into the page - the reported "muted elements are not visible". A paler
   * fill and paler ink say unavailable just as clearly and stay readable.
   */
  timeOff: {
    backgroundColor: colors.background,
    /* Also clears `timeOn`'s border - see `stripDayOff`. */
    borderColor: colors.border,
  },
  /* Marks a FULL slot apart from one that is merely too soon. Both are grey;
     only this one is gone for good. See the render. */
  timeFullText: {
    textDecorationLine: 'line-through',
  },
  /** Same inset as `notice`, but above the grid rather than under it. */
  note: {
    paddingHorizontal: screenPadding,
    marginBottom: spacing.md,
  },
  notice: {
    /* The scroller has no padding of its own - every child brings its own,
       the same as `label` and `times` above. Without this the caption ran to
       both screen edges while the grid above it sat inset. */
    paddingHorizontal: screenPadding,
    marginTop: spacing.md,
  },
  time: {
    // Three across, less the two gaps between them.
    width: `${100 / TIME_COLUMNS}%`,
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 92,
    paddingVertical: spacing.md,
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    ...hairline,
  },
  timeOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  /* Same box as a real tile, content centred, so the grid does not jump. */
  addressGate: {
    gap: spacing.md,
    paddingVertical: spacing.lg,
  },
  timeLoading: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  footer: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },

  /* Covers the whole sheet, including its footer: Confirm must not be
     reachable while the explanation for why it cannot be pressed is up. */
  /* Same padding as the grid it replaces, so the sheet keeps its height
     rather than collapsing when a day runs out. */
  dayGone: {
    gap: spacing.sm,
    paddingVertical: spacing.xl,
  },
  explainScrim: {
    /* Spelled out, not spread: this React Native version exposes
       `absoluteFill` as a registered id rather than an object, and
       `absoluteFillObject` is not on the type - the same note
       `BottomSheet` carries. */
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    /*
     * Darker than the shared `colors.overlay`, and not that token.
     *
     * `overlay` is 45% ink, tuned for a scrim over the APP - photos, cards,
     * a map. This one lands on the sheet's own near-white surface, where 45%
     * reads as a pale sage wash: the grid behind looks faded rather than
     * covered, which says 'this screen is loading' instead of 'answer the
     * question in front of you'.
     *
     * Local rather than a nudge to the token, because `overlay` is also
     * every BottomSheet's backdrop and six other overlays - darkening it
     * here would darken all of them.
     */
    backgroundColor: 'rgba(14, 42, 31, 0.72)',
    justifyContent: 'flex-end',
    padding: screenPadding,
  },
  explainCard: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
  },
  explainActions: {
    gap: spacing.sm,
    paddingTop: spacing.sm,
  },
});

export default SlotSheet;
